/**
 * Responsibility: name and tier the Discord channels the listener reads.
 *
 * **One bot, many channels.** A Discord bot is one application with one
 * token, and that token is managed with the other credentials above. There
 * is no per-channel key to organise, so what this page organises is what
 * each channel is called and how far it is trusted.
 *
 * The tier is the consequential field. It decides whether a message can
 * raise an alert on its own - an official brand channel can, a well-run
 * restock server can, a general chat channel cannot - and marking a chatty
 * server OFFICIAL is how a Discord becomes an alert storm. So it is a
 * labelled choice with its meaning written beside it, not a code to look up.
 */
import { useEffect, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import LinearProgress from '@mui/material/LinearProgress';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { ReauthRequiredError, hubApi, type DiscordChannelInput } from '../../api/client';
import { reauthenticate, secondsSinceSignIn } from '../../auth/firebase';

const KEY = ['admin', 'discord'];

/** What each tier means, in the terms the decision is actually made in. */
const TIERS: { value: DiscordChannelInput['tier']; label: string; meaning: string }[] = [
  {
    value: 'OFFICIAL',
    label: 'Official',
    meaning: 'A brand or retailer channel. One message here can raise an alert on its own.',
  },
  {
    value: 'ESTABLISHED',
    label: 'Established',
    meaning: 'A well-run restock server. Trusted to alert alone, but not a primary source.',
  },
  {
    value: 'COMMUNITY',
    label: 'Community',
    meaning:
      'General chat. Corroborates other sources but never alerts by itself - which is what keeps a busy server from becoming an alert storm.',
  },
];

const ASSUMED_REAUTH_SECONDS = 300;

export function DiscordCard({ reauthSeconds }: { reauthSeconds?: number }) {
  const client = useQueryClient();
  const state = useQuery({ queryKey: KEY, queryFn: hubApi.discordChannels });
  const [draft, setDraft] = useState<DiscordChannelInput[] | null>(null);
  const [problem, setProblem] = useState<string | null>(null);

  // Seeded once from the server, then owned locally until saved. Re-seeding
  // on every refetch would throw away a half-typed row.
  useEffect(() => {
    if (state.data && draft === null) {
      setDraft(state.data.channels.map((c) => ({ ...c })));
    }
  }, [state.data, draft]);

  const save = useMutation({
    mutationFn: async (channels: DiscordChannelInput[]) => {
      // Prove who you are before sending, not after a 401 - a popup opened
      // after an awaited request is blocked by the browser.
      const window = reauthSeconds ?? ASSUMED_REAUTH_SECONDS;
      if ((await secondsSinceSignIn()) > Math.max(window - 30, 30)) await reauthenticate();
      return hubApi.setDiscordChannels(channels);
    },
    onSuccess: (fresh) => {
      setProblem(null);
      // Reflected from what the server kept, so a dropped row is visible
      // rather than lingering on screen as though it had saved.
      setDraft(fresh.channels.map((c) => ({ ...c })));
      void client.invalidateQueries({ queryKey: KEY });
    },
    onError: (error: unknown) => {
      if (error instanceof ReauthRequiredError) {
        setProblem('Confirm it is you, then save again.');
        return;
      }
      setProblem(error instanceof Error ? error.message : String(error));
    },
  });

  const rows = draft ?? [];
  const update = (at: number, patch: Partial<DiscordChannelInput>) =>
    setDraft(rows.map((row, index) => (index === at ? { ...row, ...patch } : row)));

  return (
    <Paper variant="outlined" sx={{ p: 3 }}>
      <Stack spacing={2}>
        <Box>
          <Typography variant="h6">Discord channels</Typography>
          <Typography variant="body2" color="text.secondary">
            One bot reads every channel it has been invited to, so there is a single bot
            token - set it above as <strong>Discord bot token</strong>. This is the list of
            channels it watches, and how far each is trusted. Changes take effect when the
            hub next restarts.
          </Typography>
        </Box>
        <Divider />

        {state.isLoading && <LinearProgress />}
        {state.data && !state.data.listening && (
          <Alert severity="info">
            No bot token is set, so nothing is being read yet. The channel list can still be
            filled in now.
          </Alert>
        )}
        {problem && (
          <Alert severity="error" onClose={() => setProblem(null)}>
            {problem}
          </Alert>
        )}

        {rows.map((row, at) => (
          <Stack key={at} direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
            <TextField
              size="small"
              label="Channel ID"
              value={row.channel_id}
              onChange={(event) =>
                update(at, { channel_id: event.target.value.replace(/[^0-9]/g, '') })
              }
              sx={{ width: { xs: '100%', sm: 220 } }}
              placeholder="1234567890123456789"
            />
            <TextField
              size="small"
              label="What to call it"
              value={row.label}
              onChange={(event) => update(at, { label: event.target.value })}
              sx={{ flex: 1 }}
              placeholder="Target restocks"
            />
            <TextField
              select
              size="small"
              label="Trust"
              value={row.tier}
              onChange={(event) =>
                update(at, { tier: event.target.value as DiscordChannelInput['tier'] })
              }
              sx={{ width: { xs: '100%', sm: 170 } }}
            >
              {TIERS.map((tier) => (
                <MenuItem key={tier.value} value={tier.value}>
                  <Tooltip title={tier.meaning} placement="left">
                    <span>{tier.label}</span>
                  </Tooltip>
                </MenuItem>
              ))}
            </TextField>
            <Tooltip title="Stop watching this channel">
              <IconButton
                aria-label="Remove this channel"
                onClick={() => setDraft(rows.filter((_row, index) => index !== at))}
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        ))}

        <Stack direction="row" spacing={1}>
          <Button
            size="small"
            startIcon={<AddIcon />}
            onClick={() =>
              setDraft([...rows, { channel_id: '', label: '', tier: 'COMMUNITY' }])
            }
          >
            Add a channel
          </Button>
          <Box sx={{ flex: 1 }} />
          <Button
            variant="contained"
            size="small"
            disabled={save.isPending || draft === null}
            onClick={() => draft && save.mutate(draft.filter((row) => row.channel_id))}
          >
            {save.isPending ? 'Saving' : 'Save channels'}
          </Button>
        </Stack>

        <Typography variant="caption" color="text.secondary">
          To find a channel ID: in Discord, Settings &rarr; Advanced &rarr; Developer Mode,
          then right-click a channel and Copy Channel ID.
        </Typography>
      </Stack>
    </Paper>
  );
}
